import type { ClientRect } from "@dnd-kit/core";
import { verticalListSortingStrategy, type SortingStrategy } from "@dnd-kit/sortable";

// The stock vertical strategy counts the in-order header between My Day's blocks into the
// row gap, which throws the row next to it past the header mid-drag.
export function blockListSortingStrategy(boundary: number): SortingStrategy {
  return (args) => {
    const { activeIndex, index, rects, overIndex } = args;
    const activeRect = rects[activeIndex] ?? args.activeNodeRect;
    if (!activeRect || index === activeIndex) return verticalListSortingStrategy(args);

    const step = activeRect.height + rowGap(rects, index, activeIndex, boundary);
    const y = index > activeIndex && index <= overIndex ? -step : index < activeIndex && index >= overIndex ? step : 0;
    return { x: 0, y, scaleX: 1, scaleY: 1 };
  };
}

interface DragPositions {
  activeIndex: number;
  overIndex: number;
  rects: ClientRect[];
}

export function blockHeaderShift(boundary: number, { activeIndex, overIndex, rects }: DragPositions): number {
  if (activeIndex < 0 || overIndex < 0) return 0;
  const strategy = blockListSortingStrategy(boundary);
  const neighbour =
    activeIndex < boundary && overIndex >= boundary ? boundary : overIndex < boundary && boundary <= activeIndex ? boundary - 1 : null;
  if (neighbour === null) return 0;
  return strategy({ activeIndex, activeNodeRect: rects[activeIndex], index: neighbour, rects, overIndex })?.y ?? 0;
}

function rowGap(rects: ClientRect[], index: number, activeIndex: number, boundary: number): number {
  const current = rects[index];
  if (!current) return 0;
  const previous = index === boundary ? undefined : rects[index - 1];
  const next = index + 1 === boundary ? undefined : rects[index + 1];
  const gapBefore = previous && current.top - (previous.top + previous.height);
  const gapAfter = next && next.top - (current.top + current.height);
  return (activeIndex < index ? gapBefore ?? gapAfter : gapAfter ?? gapBefore) ?? anyRowGap(rects, boundary);
}

function anyRowGap(rects: ClientRect[], boundary: number): number {
  for (let i = 1; i < rects.length; i++) {
    if (i === boundary || !rects[i] || !rects[i - 1]) continue;
    return rects[i].top - (rects[i - 1].top + rects[i - 1].height);
  }
  return 0;
}
